/**
 * Macro #24 — global Activity notifications for the top-bar bell.
 *
 * Polls `GET /api/v1/activity/notifications` (SIWE-session authoritative) and
 * exposes the authoritative unread count + the most recent events. Fail-closed:
 * no session / any error → empty + 0. Re-checks on mount, wallet change, tab
 * focus/visibility, hash/history navigation, a bounded safety poll, and on the
 * in-app `grid:activity-read` event (fired the instant an item / all are marked
 * read) so the badge refreshes at once without waiting for the next poll.
 */
import { useCallback, useEffect, useState } from "react";
import { useAccount } from "wagmi";
import { activityApi, type ActivityEvent } from "../lib/activity-api";

const POLL_MS = 20000;
export const ACTIVITY_READ_EVENT = "grid:activity-read";

/** Fire after a notification is marked read so the bell refreshes immediately. */
export function notifyActivityRead(): void {
  window.dispatchEvent(new Event(ACTIVITY_READ_EVENT));
}

export function useActivityNotifications(): {
  unreadCount: number;
  recent: ActivityEvent[];
  refresh: () => void;
} {
  const { address } = useAccount();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recent, setRecent] = useState<ActivityEvent[]>([]);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    // Fail-closed: with no connected wallet there is no session to query — skip
    // all polling entirely (the bell is hidden anyway) so we never fire
    // authenticated requests that can only 401.
    if (!address) {
      setUnreadCount(0);
      setRecent([]);
      return;
    }
    let cancelled = false;
    const controllers: AbortController[] = [];

    const run = async () => {
      const controller = new AbortController();
      controllers.push(controller);
      try {
        const n = await activityApi.notifications(controller.signal);
        if (cancelled || controller.signal.aborted) return;
        setUnreadCount(n.unread_count ?? 0);
        setRecent(n.items ?? []);
      } catch {
        if (cancelled || controller.signal.aborted) return;
        setUnreadCount(0); // fail-closed
        setRecent([]);
      }
    };

    void run();
    const bg = () => { if (!cancelled) void run(); };
    const onVisible = () => {
      if (!cancelled && document.visibilityState === "visible") void run();
    };
    window.addEventListener("focus", bg);
    window.addEventListener("pageshow", bg);
    window.addEventListener("hashchange", bg);
    window.addEventListener("popstate", bg);
    window.addEventListener(ACTIVITY_READ_EVENT, bg);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => {
      if (!cancelled && document.visibilityState !== "hidden") void run();
    }, POLL_MS);

    return () => {
      cancelled = true;
      controllers.forEach((c) => c.abort());
      window.removeEventListener("focus", bg);
      window.removeEventListener("pageshow", bg);
      window.removeEventListener("hashchange", bg);
      window.removeEventListener("popstate", bg);
      window.removeEventListener(ACTIVITY_READ_EVENT, bg);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [address, tick]);

  return { unreadCount, recent, refresh };
}
